import type { useIntl } from 'react-intl'
import type { FlightSavedItemData } from 'tripPlanner/api/types'
import type { useIntlDateFormatter } from 'lib/datetime/dateUtils'

/** One flight leg reduced to what a shortlisted flight card renders. */
export interface LegData {
  airline?: string
  flightNumber?: string
  departureAirport?: string
  arrivalAirport?: string
  displayDate?: string
  departureDate?: string
  arrivalDate?: string
  departureTime?: string
  arrivalTime?: string
  duration?: string
  layovers?: Array<{ airportCode?: string; airportName?: string; duration?: string }>
  carrierLogo?: string
}

export function formatDurationText(duration: App.HoursMinutes | undefined): string | undefined {
  if (!duration) return undefined
  if (duration.hours === 0) return `${duration.minutes}m`
  if (duration.minutes === 0) return `${duration.hours}h`
  return `${duration.hours}h ${duration.minutes}m`
}

export function formatTimeDisplay(timeString: string): string {
  // Extract the HH:mm portion regardless of separator ("...T16:40", "... 16:40", "16:40").
  const match = timeString.match(/(\d{1,2}):(\d{2})/)
  if (match) {
    return `${match[1].padStart(2, '0')}:${match[2]}`
  }
  return timeString
}

export function getDatePart(value: string | undefined): string | undefined {
  return value?.match(/^\d{4}-\d{2}-\d{2}/)?.[0]
}

export function getSavedFlightDepartureDate(data: FlightSavedItemData): string | undefined {
  return getDatePart(data.departureDate) ?? getDatePart(data.departureTime)
}

export function getSavedFlightArrivalDate(data: FlightSavedItemData): string | undefined {
  return getDatePart(data.arrivalDate) ?? getDatePart(data.arrivalTime)
}

export function getSavedFlightLegDate(data: FlightSavedItemData, isReturnLeg: boolean): string | undefined {
  if (data.flightGroupType === 'multi_city') {
    const departureDate = getSavedFlightDepartureDate(data)
    const arrivalDate = getSavedFlightArrivalDate(data)
    if (departureDate && arrivalDate && departureDate !== arrivalDate) return `${departureDate}/${arrivalDate}`
    return departureDate ?? arrivalDate
  }

  return isReturnLeg ?
      getSavedFlightDepartureDate(data) :
      getSavedFlightArrivalDate(data)
}

export function formatLegDateLabel(date: string | undefined, dateFormatter: ReturnType<typeof useIntlDateFormatter>): string | undefined {
  if (!date) return undefined
  const [startDate, endDate] = date.split('/')
  const dateFormat = { weekday: 'short', day: 'numeric', month: 'short' } as const
  const startLabel = dateFormatter(startDate, dateFormat)
  if (!endDate || startDate === endDate) return startLabel
  return `${startLabel} - ${dateFormatter(endDate, dateFormat)}`
}

export function mergeLegData(primary: LegData, fallback: LegData | undefined): LegData {
  if (!fallback) return primary

  return {
    airline: primary.airline || fallback.airline,
    flightNumber: primary.flightNumber || fallback.flightNumber,
    departureAirport: primary.departureAirport || fallback.departureAirport,
    arrivalAirport: primary.arrivalAirport || fallback.arrivalAirport,
    displayDate: primary.displayDate || fallback.displayDate,
    departureDate: primary.departureDate || fallback.departureDate,
    arrivalDate: primary.arrivalDate || fallback.arrivalDate,
    departureTime: primary.departureTime || fallback.departureTime,
    arrivalTime: primary.arrivalTime || fallback.arrivalTime,
    duration: primary.duration || fallback.duration,
    layovers: primary.layovers ?? fallback.layovers,
    carrierLogo: primary.carrierLogo || fallback.carrierLogo,
  }
}

export function formatOccupantsLabel(
  occupancies: Array<{ adults?: number; children?: number; infants?: number }> | undefined,
  intl: ReturnType<typeof useIntl>,
): string {
  if (!occupancies?.length) return ''
  const occ = occupancies[0]
  const adults = occ.adults ?? 1
  const children = occ.children ?? 0
  const parts: Array<string> = []

  parts.push(intl.formatMessage(
    {
      id: 'sdK1Uf7spfyoDSVQjFtg',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Passenger count for shortlisted flight card header',
    },
    { count: adults },
  ))

  if (children > 0) {
    parts.push(intl.formatMessage(
      {
        id: '4mwNYERvlKnis5GUVpUB',
        defaultMessage: '{count, plural, one {# child} other {# children}}',
        description: 'Children passenger count displayed in the shortlisted flight card header section',
      },
      { count: children },
    ))
  }

  return parts.join(', ')
}
